"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  Copy,
  Check,
  ChevronRight,
  BookOpen,
  HelpCircle,
  AlertTriangle,
  ExternalLink
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Guide, GUIDES } from "@/lib/guides-data";

interface Props {
  guide: Guide;
}

export function GuideDetailContent({ guide }: Props) {
  const [copied, setCopied] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState<string>("");
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Reading progress bar scroll physics
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Extract headers for Table of Contents
  const headers = guide.sections
    .filter((sec) => sec.type === "header")
    .map((sec) => {
      const text = sec.content as string;
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      return { id, text, level: sec.level || 2 };
    });

  // Track scroll position to update active TOC header
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;
      const headingElements = contentRef.current.querySelectorAll("h2, h3");
      let currentActiveId = "";

      for (let i = 0; i < headingElements.length; i++) {
        const el = headingElements[i] as HTMLElement;
        const rect = el.getBoundingClientRect();
        // Trigger active heading slightly before it hits the top of viewport
        if (rect.top <= 120) {
          currentActiveId = el.id;
        } else {
          break;
        }
      }
      setActiveHeadingId(currentActiveId || (headers[0]?.id || ""));
    };

    window.addEventListener("scroll", handleScroll);
    // Trigger on load
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [headers]);

  // Copy page link to clipboard
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Generate Social Share Links
  const getTwitterShareUrl = () => {
    const text = encodeURIComponent(`Check out this CCMT Counseling Guide: "${guide.title}"`);
    const url = encodeURIComponent(window.location.href);
    return `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
  };

  const getLinkedInShareUrl = () => {
    const url = encodeURIComponent(window.location.href);
    return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
  };

  // Grab related guides (guides in the same category or random ones, excluding current)
  const relatedGuides = GUIDES.filter((g) => g.slug !== guide.slug)
    .sort((a, b) => (a.category === guide.category ? -1 : 1))
    .slice(0, 2);

  // Article JSON-LD Structured Data
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || "https://ccmtpredictor.com";
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": guide.title,
    "description": guide.description,
    "image": `${siteUrl}/ccmt_logo.png`,
    "datePublished": "2026-06-12T00:00:00.000Z",
    "dateModified": "2026-06-12T00:00:00.000Z",
    "author": {
      "@type": "Organization",
      "name": "CCMT College Finder Editorial Team",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "CCMT College Finder",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/ccmt_logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/counseling-guides/${guide.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-16 pb-20 overflow-x-hidden">
      {/* Injected Article JSON-LD Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      {/* Reading Progress Indicator */}
      <motion.div
        className="fixed top-16 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary z-50 origin-left"
        style={{ scaleX }}
      />

      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] left-[-15%] w-[60%] h-[40%] bg-primary/5 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[25%] right-[-15%] w-[60%] h-[40%] bg-secondary/5 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-6xl mt-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-xs font-semibold text-on-surface-variant/70 mb-8 overflow-x-auto whitespace-nowrap py-1">
          <Link href="/" className="hover:text-primary transition-colors">Home</Link>
          <ChevronRight className="size-3 flex-shrink-0" />
          <Link href="/counseling-guides" className="hover:text-primary transition-colors">Counseling Guides</Link>
          <ChevronRight className="size-3 flex-shrink-0" />
          <span className="text-foreground truncate max-w-[200px] md:max-w-[400px]">{guide.title}</span>
        </nav>

        {/* Back Link */}
        <Link href="/counseling-guides" className="inline-flex items-center gap-2 text-sm font-bold text-on-surface-variant hover:text-primary transition-colors mb-6 group">
          <ArrowLeft className="size-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to all guides
        </Link>

        {/* Header Hero */}
        <header className="mb-12">
          <span className="inline-flex px-3 py-1 text-xs font-black uppercase tracking-wider rounded-md bg-primary/10 text-primary border border-primary/20 mb-4">
            {guide.category.toUpperCase()}
          </span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-display font-black tracking-tight text-foreground leading-tight mb-6">
            {guide.title}
          </h1>
          <p className="text-base md:text-lg text-on-surface-variant/95 leading-relaxed mb-6 max-w-4xl border-l-2 border-primary/30 pl-4 py-1">
            {guide.description}
          </p>

          <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-y border-outline-variant/10 text-sm text-on-surface-variant/70">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-1.5 font-medium">
                <Calendar className="size-4 text-primary" />
                {guide.publishedDate}
              </span>
              <span className="flex items-center gap-1.5 font-medium">
                <Clock className="size-4 text-primary" />
                {guide.readTime}
              </span>
            </div>

            {/* Quick Share Widget (Mobile and Header) */}
            <div className="flex items-center gap-2">
              <span className="font-semibold text-xs flex items-center gap-1">
                <Share2 className="size-3.5" /> Share:
              </span>
              <button
                onClick={handleCopyLink}
                className="size-8 rounded-lg bg-surface-container border border-outline-variant/15 flex items-center justify-center hover:text-primary transition-all active:scale-95"
                title="Copy Link"
              >
                {copied ? <Check className="size-4 text-emerald-500" /> : <Copy className="size-4" />}
              </button>
              <a
                href={getTwitterShareUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="size-8 rounded-lg bg-surface-container border border-outline-variant/15 flex items-center justify-center hover:text-foreground transition-all active:scale-95"
                title="Share on X (Twitter)"
              >
                <svg className="size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href={getLinkedInShareUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="size-8 rounded-lg bg-surface-container border border-outline-variant/15 flex items-center justify-center hover:text-[#0A66C2] transition-all active:scale-95"
                title="Share on LinkedIn"
              >
                <svg className="size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </header>

        {/* Content Structure Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Sticky Sidebar (TOC) - Desktop Only */}
          <aside className="hidden lg:block lg:col-span-3 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto pr-4 py-2 border-r border-outline-variant/10">
            <div className="space-y-4">
              <h3 className="font-display font-black text-xs uppercase tracking-wider text-foreground flex items-center gap-2">
                <BookOpen className="size-3.5 text-primary" /> Table of Contents
              </h3>
              <nav className="flex flex-col gap-2.5">
                {headers.map((h) => (
                  <a
                    key={h.id}
                    href={`#${h.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className={`text-xs font-semibold leading-relaxed transition-all pl-2 border-l-2 py-0.5 hover:text-primary ${
                      activeHeadingId === h.id
                        ? "text-primary border-primary font-bold scale-[1.02] origin-left"
                        : "text-on-surface-variant/70 border-transparent"
                    } ${h.level === 3 ? "ml-3 pl-1 text-[11px]" : ""}`}
                  >
                    {h.text}
                  </a>
                ))}
              </nav>

              {/* Sidebar Predictor CTA Card */}
              <div className="p-4 rounded-2xl bg-primary/5 border border-primary/20 space-y-3 mt-8">
                <h4 className="font-bold text-sm text-foreground">Need Predictions?</h4>
                <p className="text-[11px] text-on-surface-variant leading-relaxed">
                  Enter your GATE score and find eligible college choices instantly.
                </p>
                <Link href="/predictor" className="block">
                  <Button size="sm" className="w-full text-xs font-bold rounded-lg h-9">
                    Go to Predictor
                  </Button>
                </Link>
              </div>
            </div>
          </aside>

          {/* Main Article Body */}
          <article ref={contentRef} className="col-span-1 lg:col-span-9 space-y-8 min-w-0">
            {guide.sections.map((section, idx) => {
              switch (section.type) {
                case "header": {
                  const text = section.content as string;
                  const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  if (section.level === 3) {
                    return (
                      <h3
                        key={idx}
                        id={id}
                        className="text-lg md:text-xl font-bold text-foreground pt-4 scroll-mt-24"
                      >
                        {text}
                      </h3>
                    );
                  }
                  return (
                    <h2
                      key={idx}
                      id={id}
                      className="text-xl md:text-2xl font-display font-black text-foreground pt-6 pb-2 border-b border-outline-variant/10 scroll-mt-24 flex items-center gap-2 group"
                    >
                      <span className="size-2 rounded-full bg-primary" />
                      {text}
                    </h2>
                  );
                }

                case "paragraph": {
                  return (
                    <p
                      key={idx}
                      className="text-sm md:text-base leading-relaxed text-on-surface-variant/95 text-justify"
                      dangerouslySetInnerHTML={{
                        __html: (section.content as string).replace(
                          /\*(.*?)\*/g,
                          "<strong class='font-bold text-foreground'>$1</strong>"
                        )
                      }}
                    />
                  );
                }

                case "list": {
                  const listItems = section.content as string[];
                  return (
                    <ul key={idx} className="space-y-3 pl-1">
                      {listItems.map((item, itemIdx) => {
                        const splitIdx = item.indexOf(":");
                        if (splitIdx !== -1) {
                          const boldPart = item.substring(0, splitIdx);
                          const restPart = item.substring(splitIdx);
                          return (
                            <li key={itemIdx} className="flex gap-3 text-sm md:text-base leading-relaxed text-on-surface-variant/95">
                              <span className="size-5 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">
                                ✓
                              </span>
                              <span>
                                <strong className="font-bold text-foreground">{boldPart}</strong>
                                {restPart}
                              </span>
                            </li>
                          );
                        }
                        return (
                          <li key={itemIdx} className="flex gap-3 text-sm md:text-base leading-relaxed text-on-surface-variant/95">
                            <span className="size-5 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-xs">
                              ✓
                            </span>
                            <span>{item}</span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                }

                case "table": {
                  const tableData = section.content as { headers: string[]; rows: string[][] };
                  return (
                    <div key={idx} className="overflow-x-auto border border-outline-variant/15 rounded-2xl bg-surface-container-low shadow-sm my-6">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-surface-container border-b border-outline-variant/15">
                            {tableData.headers.map((th, thIdx) => (
                              <th key={thIdx} className="p-4 text-xs font-black uppercase text-foreground tracking-wider whitespace-nowrap">
                                {th}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tableData.rows.map((row, rowIdx) => (
                            <tr
                              key={rowIdx}
                              className="border-b last:border-0 border-outline-variant/10 hover:bg-surface-container/30 transition-colors"
                            >
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className="p-4 text-xs md:text-sm text-on-surface-variant/90 leading-relaxed max-w-xs md:max-w-md"
                                  dangerouslySetInnerHTML={{
                                    __html: cell.replace(
                                      /\*(.*?)\*/g,
                                      "<strong class='font-bold text-foreground'>$1</strong>"
                                    )
                                  }}
                                />
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                default:
                  return null;
              }
            })}

            {/* Official Source Disclaimer Section */}
            <section className="p-6 rounded-2xl bg-surface-container-low border border-amber-500/10 dark:bg-amber-500/5 dark:border-amber-500/20 space-y-3 mt-12">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-bold text-sm">
                <AlertTriangle className="size-5" />
                <span>Official Source Disclaimer</span>
              </div>
              <p className="text-xs text-on-surface-variant leading-relaxed">
                This guide is compiled by students and educators for informational guidance purposes only. The official counseling procedures, qualifying scores, reservation criteria, and schedules are determined solely by the CCMT Central Board. Candidates are strongly advised to verify all critical steps on the official CCMT portal (
                <a
                  href="https://ccmt.admissions.nic.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-0.5"
                >
                  ccmt.admissions.nic.in <ExternalLink className="size-2.5" />
                </a>
                ) before taking any decision.
              </p>
            </section>

            {/* Related Articles Footer Section */}
            <section className="pt-12 border-t border-outline-variant/10 space-y-6">
              <h3 className="font-display font-black text-lg text-foreground flex items-center gap-2">
                <BookOpen className="size-5 text-primary" /> Keep Reading & Exploring
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedGuides.map((g) => (
                  <Link
                    key={g.slug}
                    href={`/counseling-guides/${g.slug}`}
                    className="p-5 rounded-2xl bg-surface-container border border-outline-variant/10 hover:border-primary/20 transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[10px] font-black uppercase text-primary tracking-wider mb-2 block">
                        {g.category}
                      </span>
                      <h4 className="font-bold text-sm text-foreground line-clamp-1 group-hover:text-primary mb-1">
                        {g.title}
                      </h4>
                      <p className="text-xs text-on-surface-variant line-clamp-2 leading-relaxed">
                        {g.description}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-primary mt-4 flex items-center gap-1">
                      Read Guide <ChevronRight className="size-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}
